
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api.jsx';
import './Navbar.css';

const Navbar = () => {
  const {
    isAuthenticated,
    user,
    userPhoto,
    logout,
    effectiveRole,
    roleOverride,
    setRoleOverride,
    viewAsUser,
    applyViewAsUser,
    clearViewAsUser,
    isViewingAsUser,
    canViewAsUsers,
    hasPermission,
    hasAnyPermission
  } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [marketingDropdownOpen, setMarketingDropdownOpen] = useState(false);
  const [eventsDropdownOpen, setEventsDropdownOpen] = useState(false);
  const [websiteDropdownOpen, setWebsiteDropdownOpen] = useState(false);
  const [viewableUsers, setViewableUsers] = useState([]);
  const [databaseEnvironment, setDatabaseEnvironment] = useState('');

  useEffect(() => {
    if (isAuthenticated && canViewAsUsers) {
      loadViewableUsers();
    }
  }, [isAuthenticated, canViewAsUsers]);

  useEffect(() => {
    if (!isAuthenticated) {
      setDatabaseEnvironment('');
      return;
    }

    loadDatabaseEnvironment();
  }, [isAuthenticated]);

  const loadViewableUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      const payload = response.data;
      if (Array.isArray(payload)) {
        setViewableUsers(payload);
        return;
      }
      if (Array.isArray(payload?.users)) {
        setViewableUsers(payload.users);
        return;
      }
      setViewableUsers([]);
    } catch (err) {
      console.error('Failed to load users for View As:', err);
      setViewableUsers([]);
    }
  };

  const loadDatabaseEnvironment = async () => {
    try {
      const response = await api.get('/environment');
      const env = String(response?.data?.databaseEnvironment || '').toLowerCase();
      setDatabaseEnvironment(env);
    } catch (err) {
      console.error('Failed to load database environment:', err);
      setDatabaseEnvironment('');
    }
  };

  const canViewContacts = hasAnyPermission(['contacts:read:own', 'contacts:read:any']);
  const canViewTemplates = hasAnyPermission(['templates:read:own', 'templates:read:any']);
  const canViewTemplateImages = hasAnyPermission(['template-images:read:own', 'template-images:read:any']);
  const canViewCampaigns = hasAnyPermission(['campaigns:read:own', 'campaigns:read:any']);
  const canViewEvents = hasAnyPermission(['events:read:own', 'events:read:any']);
  const canViewNews = hasPermission('news:read');
  const canViewCareers = hasPermission('careers:read');
  const canViewTeamMembers = hasPermission('team-members:read');
  const canUseRateCalculator = hasPermission('rate-calculator:use');
  const canViewNcfs = hasAnyPermission(['ncf:read:own', 'ncf:read:any', 'ncf:admin:access']);
  const canUseSignatureBuilder = hasPermission('signature-builder:use');
  const canUseVideoBackgrounds = hasPermission('video-backgrounds:use');
  const canViewBilling = hasPermission('billing:read');
  const canAccessAdmin = hasAnyPermission(['admin:users:read', 'admin:users:write', 'admin:permissions:write', 'admin:ops:emailworker']);

  return (
    <nav className="navbar">
      <div className="navbar-brand">
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img 
            src="https://rscwebsitestorage.blob.core.windows.net/$web/RSC Toolbox_1024x600_white.png" 
            alt="RSC Toolbox" 
            style={{ height: '125px', marginRight: '15px' }}
          />
        </Link>
        {databaseEnvironment === 'development' && (
          <span className="environment-badge">development</span>
        )}
      </div>

      {/* Hamburger menu button for mobile */}
      {isAuthenticated && (
        <button 
          className="hamburger-menu"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      )}

      {isAuthenticated && (
        <ul className={`navbar-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <li><Link to="/" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link></li>
          
          {/* Marketing dropdown */}
          {(canViewContacts || canViewTemplates || canViewTemplateImages || canViewCampaigns) && (
            <li className="dropdown"
                onMouseEnter={() => setMarketingDropdownOpen(true)}
                onMouseLeave={() => setMarketingDropdownOpen(false)}>
              <span className="dropdown-toggle">Marketing ▾</span>
              <ul className={`dropdown-menu ${marketingDropdownOpen ? 'show' : ''}`}>
                <li><Link to="/marketing-dashboard" onClick={() => { setMobileMenuOpen(false); setMarketingDropdownOpen(false); }}>Marketing Dashboard</Link></li>
                {canViewContacts && (
                  <li><Link to="/contacts" onClick={() => { setMobileMenuOpen(false); setMarketingDropdownOpen(false); }}>Contacts</Link></li>
                )}
                {canViewTemplates && (
                  <li><Link to="/templates" onClick={() => { setMobileMenuOpen(false); setMarketingDropdownOpen(false); }}>Templates</Link></li>
                )}
                {canViewTemplateImages && (
                  <li><Link to="/template-images" onClick={() => { setMobileMenuOpen(false); setMarketingDropdownOpen(false); }}>Template Images</Link></li>
                )}
                {canViewCampaigns && (
                  <li><Link to="/campaigns" onClick={() => { setMobileMenuOpen(false); setMarketingDropdownOpen(false); }}>Campaigns</Link></li>
                )}
              </ul>
            </li>
          )}
          
          {/* Events dropdown */}
          {canViewEvents && (
            <li className="dropdown"
                onMouseEnter={() => setEventsDropdownOpen(true)}
                onMouseLeave={() => setEventsDropdownOpen(false)}>
              <span className="dropdown-toggle">Events ▾</span>
              <ul className={`dropdown-menu ${eventsDropdownOpen ? 'show' : ''}`}>
                <li><Link to="/events" onClick={() => { setMobileMenuOpen(false); setEventsDropdownOpen(false); }}>Events</Link></li>
                <li><Link to="/registrations" onClick={() => { setMobileMenuOpen(false); setEventsDropdownOpen(false); }}>Registrations</Link></li>
              </ul>
            </li>
          )}
          
          {/* Website dropdown */}
          {(canViewNews || canViewCareers || canViewTeamMembers) && (
            <li className="dropdown"
                onMouseEnter={() => setWebsiteDropdownOpen(true)}
                onMouseLeave={() => setWebsiteDropdownOpen(false)}>
              <span className="dropdown-toggle">Website ▾</span>
              <ul className={`dropdown-menu ${websiteDropdownOpen ? 'show' : ''}`}>
                {canViewNews && (
                  <li><Link to="/news" onClick={() => { setMobileMenuOpen(false); setWebsiteDropdownOpen(false); }}>News</Link></li>
                )}
                {canViewCareers && (
                  <li><Link to="/careers" onClick={() => { setMobileMenuOpen(false); setWebsiteDropdownOpen(false); }}>Careers</Link></li>
                )}
                {canViewTeamMembers && (
                  <li><Link to="/team-members" onClick={() => { setMobileMenuOpen(false); setWebsiteDropdownOpen(false); }}>Team Members</Link></li>
                )}
              </ul>
            </li>
          )}
          
          {canUseRateCalculator && <li><Link to="/rate-calculator" onClick={() => setMobileMenuOpen(false)}>Rate Calculator</Link></li>}
          {canViewNcfs && <li><Link to="/ncfs" onClick={() => setMobileMenuOpen(false)}>NCFs</Link></li>}
          {canViewBilling && <li><Link to="/billing" onClick={() => setMobileMenuOpen(false)}>Billing</Link></li>}
          {canUseSignatureBuilder && <li><Link to="/signature-builder" onClick={() => setMobileMenuOpen(false)}>Signature Builder</Link></li>}
          {canUseVideoBackgrounds && <li><Link to="/video-backgrounds" onClick={() => setMobileMenuOpen(false)}>Meeting Backgrounds</Link></li>}
          {canAccessAdmin && (
            <li><Link to="/admin" onClick={() => setMobileMenuOpen(false)}>Admin</Link></li>
          )}
          
          {/* Mobile user info section */}
          <li className="mobile-user-section">
            <div className="mobile-user-name">{user?.displayName || user?.mail}</div>
            {canViewAsUsers && !isViewingAsUser && (
              <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '8px' }}>
                Current role: {effectiveRole}
              </div>
            )}
            {canViewAsUsers && isViewingAsUser && (
              <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '8px' }}>
                Viewing as: {viewAsUser?.displayName || viewAsUser?.email} ({effectiveRole})
              </div>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="btn btn-secondary" style={{ width: '100%' }}>
              Logout
            </button>
          </li>
        </ul>
      )}

      <div className="navbar-user">
        {/* Role switcher for users with multiple roles or superuser preview */}
        {isAuthenticated && (
          <>
            {/* Show role selector for superuser (can preview any role) */}
            {canViewAsUsers && (
              <select
                value={isViewingAsUser ? (viewAsUser?.userId || '') : ''}
                onChange={async e => {
                  const selectedUserId = e.target.value;
                  if (!selectedUserId) {
                    clearViewAsUser();
                    return;
                  }

                  const selectedUser = viewableUsers.find(candidate => String(candidate.UserId || '') === selectedUserId);
                  if (!selectedUser) {
                    clearViewAsUser();
                    return;
                  }

                  await applyViewAsUser(selectedUser);
                }}
                className="role-select view-as-select"
                title="View as user"
              >
                <option value="">My View (actual)</option>
                {viewableUsers
                  .filter(candidate => (candidate.Role || '').toLowerCase() !== 'superuser' && candidate.IsActive !== false)
                  .map(candidate => (
                    <option key={candidate.UserId} value={candidate.UserId}>
                      {candidate.DisplayName || candidate.Email} ({candidate.Role || 'user'})
                    </option>
                  ))}
              </select>
            )}
            
            {/* Show role selector for users with multiple roles */}
            {user?.role && !user.role.startsWith('superuser') && user.role.includes(',') && (
              <select
                value={roleOverride || user.role.split(',')[0]}
                onChange={async e => {
                  const primaryRole = user.role.split(',')[0];
                  const selectedRole = e.target.value;
                  await setRoleOverride(selectedRole === primaryRole ? null : selectedRole);
                }}
                className="role-select"
                title="Preview as role"
              >
                {user.role.split(',').map((role, idx) => (
                  <option key={idx} value={role.trim()}>
                    {role.trim()} {idx === 0 ? '(primary)' : ''}
                  </option>
                ))}
              </select>
            )}
          </>
        )}
        {isAuthenticated && (
          <div className="user-dropdown-container">
            <button 
              className="user-icon-btn"
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              title={user?.displayName || user?.mail}
            >
              {userPhoto ? (
                <img src={userPhoto} alt="User" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                '👤'
              )}
            </button>
            {userDropdownOpen && (
              <div className="user-dropdown-menu">
                <div className="user-info">{user?.displayName || user?.mail}</div>
                <button 
                  className="dropdown-logout-btn"
                  onClick={() => {
                    logout();
                    setUserDropdownOpen(false);
                  }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
        {isAuthenticated && canViewAsUsers && (
          <div className="view-as-indicator" title={isViewingAsUser && viewAsUser?.source?.permissions ? `Permissions resolved from: ${viewAsUser.source.permissions}` : undefined}>
            {isViewingAsUser
              ? `Viewing as ${viewAsUser?.displayName || viewAsUser?.email} • Access: ${effectiveRole}`
              : `Access: ${effectiveRole}`}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
